import React, { useRef, useState, useEffect } from 'react';
import {
  useIsChartPaneOpen,
  useSetChartPaneOpen,
  useGetPredictionsStatus,
  useGetLastOraclePrice,
  useGetCurrentEpoch,
  useGetRounds,
  useGetRound,
} from './context/PredictionsContext';
import TradingView from './components/TradingView';
import History from './History';
import Positions from './Positions';
import ErrorNotification from './components/Notification/ErrorNotification';
import PauseNotification from './components/Notification/PauseNotification';
import bitcoin from '../../assets/utility/bitcoin.png';
import { FaClock, FaCopy, FaHistory, FaChartLine, FaArrowLeft, FaArrowRight, FaChartBar, FaArrowUp,FaArrowDown } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import { useUSDTBalance } from './hooks/useUSDTBalance';
import { useBullvsBearContract } from './hooks/useBullvsBearContract';
import { useAccount } from 'wagmi';
import CountdownTimer from './components/CountdownTimer';
import { useSearchParams } from 'react-router-dom';

const Desktop = () => {
  const splitWrapperRef = useRef();
  const chartRef = useRef();
  const gutterRef = useRef();
  const cardRowRef = useRef();
  const isChartPaneOpen = useIsChartPaneOpen();
  const setIsChartPaneOpen = useSetChartPaneOpen();
  const status = useGetPredictionsStatus();
  const lastOraclePrice = useGetLastOraclePrice?.();
  const currentEpoch = useGetCurrentEpoch?.();
  const rounds = useGetRounds?.();
  const { address } = useAccount();
  const { getUSDTBalance } = useUSDTBalance();
  const { fetchContractData } = useBullvsBearContract();

  const [isHistoryPaneOpen, setIsHistoryPaneOpen] = useState(false);
  const [usdtBalance, setUsdtBalance] = useState("0.00");
  const [referralLink, setReferralLink] = useState("Connect your wallet to get your referral link");
  const [copied, setCopied] = useState(false);
  const [searchParams] = useSearchParams(); 
  const [referralAddress, setReferralAddress] = useState(null);

  // Card slider state
  const [cardIndex, setCardIndex] = useState(0);

    useEffect(() => {
    // Check for referral parameter in URL
    const refParam = searchParams.get('ref');
    if (refParam && refParam !== address) { // Don't refer yourself
      setReferralAddress(refParam);
      console.log('Referral detected:', refParam);
    }
  }, [searchParams, address]);


  useEffect(() => {
    if (address) {
      setReferralLink(`${window.location.origin}/#/prediction?ref=${address}`);
      const fetchBalance = async () => {
        const balance = await getUSDTBalance(address);
        setUsdtBalance(balance);
      };
      fetchBalance();
      fetchContractData();
    }
  }, [address, getUSDTBalance, fetchContractData]);

  // Use the rounds object mapping for the current round
  const currentRound = useGetRound(currentEpoch);

  const roundTime = currentRound?.lockTimestamp
    ? new Date(Number(currentRound.lockTimestamp) * 1000).toLocaleTimeString()
    : '--';

  let winner = null;
  let winnerStatus = null;

  if (currentRound && lastOraclePrice) {
    if (currentRound.closePrice && currentRound.lockPrice) {
      const closePrice = Number(currentRound.closePrice);
      const lockPrice = Number(currentRound.lockPrice);

      if (closePrice > lockPrice) {
        winner = 'UP';
        winnerStatus = 'win';
      } else if (closePrice < lockPrice) {
        winner = 'DOWN';
        winnerStatus = 'win';
      } else {
        winner = 'TIE';
        winnerStatus = 'tie';
      }
    } else if (currentRound.lockPrice) {
      const currentPrice = Number(lastOraclePrice);
      const lockPrice = Number(currentRound.lockPrice);

      if (currentPrice > lockPrice) {
        winner = 'UP';
        winnerStatus = 'live';
      } else if (currentPrice < lockPrice) {
        winner = 'DOWN';
        winnerStatus = 'live';
      } else {
        winner = 'TIE';
        winnerStatus = 'live';
      }
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Cards for slider (replace or add more as needed)
  const cardData = [
    { title: 'Your Positions', content: <Positions /> },
    // Add more cards here if needed:
    // { title: 'Analytics', content: <Analytics /> },
    // { title: 'Stats', content: <Stats /> },
  ];

   const [scrollPosition, setScrollPosition] = useState(0);
    const [maxScrollWidth, setMaxScrollWidth] = useState(0);

  // Card slider navigation
  const handlePrev = () => {
    const container = cardRowRef.current;
    if (container) {
      const cardWidth = 320; // Card width (288px) + gap (32px)
      const newPosition = Math.max(0, container.scrollLeft - cardWidth);
      container.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
      console.log('Scrolling left, current scrollLeft:', container.scrollLeft, 'new position:', newPosition);
    }
  };
  
  const handleNext = () => {
    const container = cardRowRef.current;
    if (container) {
      const cardWidth = 320; // Card width (288px) + gap (32px)
      const maxScroll = container.scrollWidth - container.clientWidth;
      const newPosition = Math.min(maxScroll, container.scrollLeft + cardWidth);
      container.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
      console.log('Scrolling right, current scrollLeft:', container.scrollLeft, 'new position:', newPosition, 'maxScroll:', maxScroll);
    }
  };

  // CORREÇÃO: Fix scroll position tracking
  useEffect(() => {
    const container = cardRowRef.current;
    if (container) {
      const handleScroll = () => {
        const currentScroll = container.scrollLeft;
        const maxScroll = container.scrollWidth - container.clientWidth;
        setScrollPosition(currentScroll);
        setMaxScrollWidth(maxScroll);
        console.log('Scroll event - position:', currentScroll, 'max:', maxScroll);
      };
      
      // Initial setup
      handleScroll();
      
      container.addEventListener('scroll', handleScroll);
      
      // Also listen for resize to recalculate max scroll
      const handleResize = () => {
        setTimeout(handleScroll, 100); // Delay to ensure layout is updated
      };
      window.addEventListener('resize', handleResize);
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  const isAtStart = scrollPosition <= 5; // Small tolerance for floating point
  const isAtEnd = maxScrollWidth > 0 && scrollPosition >= (maxScrollWidth - 5);

  
  // Custom scrollbar styles (global for this component)
  const customScrollbarStyle = `
    .custom-scrollbar::-webkit-scrollbar { 
      height: 6px; 
      width: 6px;
      background: transparent; 
    }
    .custom-scrollbar::-webkit-scrollbar-thumb { 
      background: #7900ff; 
      border-radius: 6px; 
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    .custom-scrollbar { 
      scrollbar-color: #7900ff transparent; 
      scrollbar-width: thin; 
    }
  `;

  return (
    <div className="flex flex-col h-screen bg-black">
      <style>{customScrollbarStyle}</style>
      {/* Header */}
      <div className="w-full flex items-center gap-3 px-8 py-3 bg-[#18181b] border-b border-[#222] sticky top-0 z-50">
        {/* Referral Card */}
        <div className="flex-1">
          <div className="bg-gradient-to-r from-[#2a2a2d] to-[#222] rounded-xl px-6 py-4 flex flex-col md:flex-row items-center gap-4 shadow-lg">
            <div className="flex-1">
              <span className="text-white font-semibold flex items-center gap-2">
                <FaHistory className="text-[#7900ff]" />
                Your referral link
              </span>
              <div className="flex items-center mt-2">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  className="bg-[#18181b] text-gray-200 px-3 py-2 rounded-l-md border border-[#333] w-full md:w-80 text-sm focus:outline-none"
                />
                <button
                  onClick={handleCopy}
                  className="bg-[#7900ff] hover:bg-[#a259ff] text-white px-4 py-2 rounded-r-md font-bold transition-colors flex items-center gap-2"
                >
                  <FaCopy size={14} />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end">
              <span className="text-gray-400 text-sm flex items-center gap-2">
                <SiTether className="text-[#26A17B]" /> USDT Balance
              </span>
              <span className="text-2xl font-bold text-green-400">
                {usdtBalance}
              </span>
            </div>
          </div>
        </div>
        {/* BTC Price */}
        <div className="bg-[#222] rounded-xl px-4 py-3 flex flex-col items-center justify-center min-w-[180px]">
          <img src={bitcoin} alt="Bitcoin" className="w-7 h-7 mb-1" />
          <span className="text-sm text-gray-400 text-center">BTC/USDT</span>
          <span className="text-xl font-bold text-[#F7931A] text-center">
            {lastOraclePrice ? `$${Number(lastOraclePrice).toLocaleString()}` : '--'}
          </span>
        </div>
        {/* Winner Indicator */}
        {winner && (
          <div className="px-4 py-2 rounded-xl font-bold flex items-center gap-2 bg-transparent border border-gray-700/50">
            {winner === 'UP' && <FaArrowUp className="text-green-500" />}
            {winner === 'DOWN' && <FaArrowDown className="text-red-500" />}
            {winner === 'TIE' && null}

            <span className={`${
              winner === 'UP' 
                ? 'text-green-500' 
                : winner === 'DOWN' 
                ? 'text-red-500' 
                : 'text-yellow-500'
            }`}>
              {winner === 'UP' && 'UP'}
              {winner === 'DOWN' && 'DOWN'}
              {winner === 'TIE' && '⚊ TIE'}
            </span>

            {/* Indicador de status - colorido também */}
            {winnerStatus === 'live' && (
              <div className={`w-2 h-2 rounded-full animate-pulse ml-1 ${
                winner === 'UP' 
                  ? 'bg-green-500' 
                  : winner === 'DOWN' 
                  ? 'bg-red-500' 
                  : 'bg-yellow-500'
              }`}></div>
            )}
          </div>
        )}

        <button
          onClick={() => setIsHistoryPaneOpen(!isHistoryPaneOpen)}
          className="p-2 rounded-lg bg-[#222] hover:bg-[#333] transition-colors"
        >
          <FaHistory className="text-[#7900ff]" size={20} />
        </button>

        {/* Round Timer with Countdown */}
        <div className="ml-auto flex items-center gap-3 bg-[#7900ff] px-3 py-1 rounded-xl shadow-lg">
          <FaClock className="text-white" />
          <span className="text-white font-medium">
            #{currentEpoch - 1 ?? '--'}
          </span>
          <CountdownTimer status="live" type="close" />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative h-full">
        <div
          ref={splitWrapperRef}
          className="flex-1 min-w-0 flex flex-col"
          style={{
            height: 'calc(100vh - 88px)',
          }}
        >
          {/* Chart */}
          <div
            className={`relative flex-shrink-0 transition-all duration-300`}
            style={{
              minHeight: isChartPaneOpen ? 200 : 0,
              height: isChartPaneOpen ? '40vh' : 0,
              maxHeight: isChartPaneOpen ? '50vh' : 0,
              overflow: isChartPaneOpen ? 'visible' : 'hidden',
              opacity: isChartPaneOpen ? 1 : 0,
              pointerEvents: isChartPaneOpen ? 'auto' : 'none',
            }}
          >
            <div className="absolute inset-0 w-full h-full">
              <TradingView />
            </div>
          </div>

          {/* Resizer */}
          {isChartPaneOpen && (
            <div
              ref={gutterRef}
              className="bg-[#1a1a1a] cursor-row-resize h-3 relative"
            >
              <div className="absolute h-1 w-16 bg-[#7900ff] rounded-lg left-1/2 top-1 -ml-8"></div>
            </div>
          )}

          {/* Card Slider */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {(status === 'error' && <ErrorNotification />) ||
              (status === 'paused' && <PauseNotification />) ||
              (status === 'live' && (
                <div className="relative flex flex-col overflow-hidden h-full">
                  <div className="flex items-center justify-between px-2 py-4">
                            
                    {/* Chart Toggle Button */}
                    <div className="ml-2 flex items-center gap-2">
                      <button
                        onClick={() => setIsChartPaneOpen(!isChartPaneOpen)}
                        className="px-4 py-2 rounded-xl bg-[#222] hover:bg-[#7900ff] text-white font-semibold transition-colors flex items-center gap-2"
                      >
                        <FaChartBar />
                        {isChartPaneOpen ? 'Hide Chart' : 'Show Chart'}
                      </button>
                    </div>
                    
                  </div>
                  

                  <div
                    ref={cardRowRef}
                    className="flex-1 overflow-x-auto overflow-y-hidden custom-scrollbar"
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#7900ff transparent',
                    }}
                  >
                    <div className="h-full px-4 py-2">
                      <Positions referralAddress={referralAddress} />
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* History Panel */}
        <div
          className={`h-[calc(100vh-88px)] transition-all duration-200 ease-in-out border-l border-[#222] overflow-y-auto ${isHistoryPaneOpen ? 'w-96 opacity-100' : 'w-0 opacity-0'
            }`}
        >
          <History />
        </div>
      </div>
    </div>
  );
};

export default Desktop;